import type { TenantStatus } from '@/types/platform-auth.types';
import { cn } from '@/lib/cn';

/**
 * Phase 12.3G — shared Companies presentation.
 *
 * One badge and one date formatter, used by the list, the detail page and the
 * create flow. Previously each page kept its own copy of the status palette,
 * which is how two surfaces end up disagreeing about what "Suspended" looks
 * like.
 */

const STATUS_BADGE: Record<TenantStatus, string> = {
  ACTIVE: 'bg-green-500/10 text-green-700 dark:text-green-400',
  TRIAL: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  SUSPENDED: 'bg-destructive/10 text-destructive',
  CANCELLED: 'bg-muted text-muted-foreground',
};

/**
 * §6.2 — the words the console uses for each state.
 *
 * §5.5 requires status to be conveyed by TEXT as well as colour, so the badge
 * always renders a label. Someone who cannot distinguish the amber from the
 * green still reads "Trial" and "Active".
 */
const STATUS_LABEL: Record<TenantStatus, string> = {
  ACTIVE: 'Active',
  TRIAL: 'Trial',
  SUSPENDED: 'Suspended',
  CANCELLED: 'Cancelled',
};

export function CompanyStatusBadge({
  status,
  size = 'sm',
}: {
  status: TenantStatus;
  size?: 'sm' | 'md';
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full font-medium',
        size === 'md' ? 'px-3 py-1 text-xs font-semibold' : 'px-2.5 py-0.5 text-xs',
        STATUS_BADGE[status],
      )}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

export function statusLabel(status: TenantStatus): string {
  return STATUS_LABEL[status];
}

/** Date only — used in the list's Created column. */
export function formatCompanyDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Date and time — used on Company Detail, where "when exactly" matters. */
export function formatCompanyDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
